<script setup lang="ts">
import { computed, onMounted, ref, watch } from '#imports';
import { useEventListener, useMediaQuery } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { defaultLocalePath } from '~/constants/appLocale';
import { blogHomePath, blogLocaleFor } from '~/constants/blog';
import { isMarketingPath } from '~/constants/featurePages';
import { useProfileStore } from '~/stores/profileState';

/**
 * Site header in the template's shape: logo left, the main menu centred on
 * desktop, one pill button on the right, and a hamburger below the `nav`
 * breakpoint that opens an off-canvas panel sliding in from the right.
 *
 * Once the page scrolls the header gets the template's blur and hairline shadow.
 */
const user = useSupabaseUser();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const profileStore = useProfileStore();

const { isPremium, exportCredits, isLoading } = usePremiumStatus();

/**
 * The server, and the first client render, always produce the anonymous menu.
 *
 * Two reasons. First, that menu is the one containing /blog and /demo, so those
 * links end up in the server HTML that crawlers see. Second, it makes the shell
 * auth-independent, which is a precondition for CDN-caching these pages at all:
 * Vercel's edge cache keys on path and Nuxt emits no `Vary: cookie`, so a
 * logged-in navbar in the cache would be served to every anonymous visitor.
 *
 * Because `hydrated` is false during both SSR and the first client render, there
 * is no hydration mismatch; real auth state appears one tick later.
 */
const hydrated = ref(false);
const scrolled = ref(false);

onMounted(() => {
  hydrated.value = true;
  scrolled.value = window.scrollY > 0;
});

if (import.meta.client) {
  useEventListener(window, 'scroll', () => { scrolled.value = window.scrollY > 0; }, { passive: true });
}

const isLoggedIn = computed(() => hydrated.value && !!user.value);
const isProfileComplete = computed(() => !profileStore.isProfileIncomplete);
// Homepage plus every feature/pricing subpage carved out of it.
const isMarketingPage = computed(() => isMarketingPath(route.path));
const localize = useLocalizedPath();

// Marketing pages always show the presentation menu; the app menu appears only
// for signed-in users with a complete profile, outside marketing pages.
const showPresentationMenu = computed(() => isMarketingPage.value || !isLoggedIn.value);
const showAdminMenu = computed(() => isLoggedIn.value && isProfileComplete.value && !isMarketingPage.value);

/**
 * Below this breakpoint the header shrinks to logo, account menu and hamburger,
 * and everything else (links, premium, credits, My CVs) moves into the
 * off-canvas panel. The presentation menu needs the full `nav` width for its
 * centred links; the app menu fits down to `sm`.
 */
const mobileOnlyClass = computed(() => (showAdminMenu.value ? 'sm:hidden' : 'nav:hidden'));

const isMenuOpen = ref(false);
const isFeaturesOpen = ref(false);

/**
 * Desktop/mobile is a CSS concern (the `nav:` breakpoint, 1025px). This ref
 * survives only for FeaturesDropdown, which forks into two structurally
 * different trees (one containing a Teleport) and is rendered inside ClientOnly.
 */
const isFeaturesDropdownDesktop = useMediaQuery('(min-width: 1025px)');

/** Primary links, in the template's order. All are real routes. */
const links = computed(() => [
  { id: 'ai', to: '/ai-features', label: t('sofax.nav.aiFeatures') },
  { id: 'pricing', to: '/pricing', label: t('sofax.nav.pricing') },
  { id: 'demo', to: '/demo', label: t('sofax.nav.demo') },
  // Czech and German readers get their own blog, everyone else the English one.
  { id: 'blog', to: blogHomePath(blogLocaleFor(locale.value)), label: t('sofax.nav.blog') },
]);

function isActive(to: string) {
  const path = defaultLocalePath(to);
  const current = defaultLocalePath(route.path);
  return current === path || current.startsWith(`${path}/`);
}

function closeMenu() {
  isMenuOpen.value = false;
  isFeaturesOpen.value = false;
}

// Close the off-canvas panel on navigation and lock body scroll while it is open.
watch(() => route.fullPath, closeMenu);

if (import.meta.client) {
  watch(isMenuOpen, (open) => {
    document.documentElement.classList.toggle('overflow-hidden', open);
  });
  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') closeMenu();
  });
}

const linkClass = 'inline-flex items-center gap-1 whitespace-nowrap font-body text-base font-semibold text-ink transition-colors duration-300 hover:text-accent dark:text-white dark:hover:text-accent-soft';
const mobileLinkClass = 'flex w-full items-center justify-between py-4 font-title text-lg font-semibold text-ink dark:text-white';
</script>

<template>
  <header
    class="sofax sofax-header sticky top-0 z-50 w-full border-b border-transparent bg-white/95 transition-all duration-300 dark:bg-gray-950/95"
    :class="{ 'scrolling border-line dark:border-gray-800': scrolled }"
  >
    <div class="shell flex items-center justify-between gap-6 py-3 lg:py-4">
      <!-- Logo -->
      <LocaleLink to="/" class="flex shrink-0 items-center" :aria-label="t('common.logo')">
        <NuxtImg src="/logo_white.svg" :alt="t('common.logo')" width="264" height="56" class="-my-1 hidden h-11 w-auto lg:h-12 dark:block" />
        <NuxtImg src="/logo.svg" :alt="t('common.logo')" width="264" height="56" class="-my-1 block h-11 w-auto lg:h-12 dark:hidden" />
      </LocaleLink>

      <!-- ===== Presentation menu ===== -->
      <template v-if="showPresentationMenu">
        <!--
          Desktop menu, centred as in the template. Always in the server HTML;
          hidden below the `nav` breakpoint purely by CSS.
        -->
        <nav class="hidden nav:block" :aria-label="t('sofax.nav.menuTitle')">
          <ul class="flex items-center gap-x-[1.875rem]">
            <li>
              <!--
                ClientOnly: this component forks into two structurally different
                trees (the desktop one contains a Teleport). No SEO cost: every
                feature page is also linked from the footer.
              -->
              <ClientOnly>
                <NavbarFeaturesDropdown
                  :is-desktop="isFeaturesDropdownDesktop"
                  :is-open="isFeaturesOpen"
                  @update:is-open="isFeaturesOpen = $event"
                  @close="closeMenu"
                />
                <template #fallback>
                  <span :class="linkClass">{{ t('sofax.nav.features') }} <i class="bi bi-chevron-down text-xs" /></span>
                </template>
              </ClientOnly>
            </li>
            <li v-for="link in links" :key="link.id">
              <LocaleLink :to="link.to" :class="[linkClass, { 'text-accent dark:text-accent-soft': isActive(link.to) }]">
                {{ link.label }}
              </LocaleLink>
            </li>
          </ul>
        </nav>

        <!-- Right block: one pill button, account, hamburger -->
        <div class="flex items-center gap-x-3 sm:gap-x-5">
          <template v-if="!isLoggedIn">
            <LocaleLink to="/login" :class="[linkClass, 'hidden nav:inline-flex']">{{ t('sofax.nav.login') }}</LocaleLink>
            <SofaxBtn to="/free-cv-builder" :label="t('sofax.nav.buildCv')" variant="accent" class="btn-sm !hidden sm:!inline-flex" />
          </template>

          <template v-else>
            <LocaleLink
              v-if="!isPremium && !isLoading"
              to="/admin/premium"
              :class="[linkClass, 'group hidden nav:inline-flex']"
            >
              <i class="bi bi-star-fill text-yellow-400 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
              {{ t('sofax.nav.premium') }}
            </LocaleLink>
            <LocaleLink to="/applications" :class="[linkClass, 'hidden nav:inline-flex']">{{ t('applications.nav') }}</LocaleLink>
            <SofaxBtn to="/cv" :label="t('sofax.nav.myCvs')" variant="accent" class="btn-sm !hidden whitespace-nowrap sm:!inline-flex" />
            <NavbarUserMenu />
          </template>

          <div class="nav:hidden">
            <CommonHamburgerMenu v-model:is-open="isMenuOpen" />
          </div>
        </div>
      </template>

      <!-- ===== App menu (signed in, complete profile, outside marketing pages) ===== -->
      <template v-if="showAdminMenu">
        <div class="flex items-center gap-x-3 sm:gap-x-5">
          <LocaleLink
            v-if="!isPremium && !isLoading"
            to="/admin/premium"
            :class="[linkClass, 'group hidden sm:inline-flex']"
            :title="t('sofax.nav.premium')"
          >
            <i class="bi bi-star-fill text-yellow-400 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
            {{ t('sofax.nav.premium') }}
          </LocaleLink>

          <!-- Export credits badge (on mobile it lives in the off-canvas panel) -->
          <div
            v-if="!isLoading && exportCredits > 0"
            class="group relative hidden cursor-default items-center gap-1.5 rounded-full bg-accent-soft/40 px-3 py-1 text-xs font-bold text-ink sm:flex dark:bg-accent/20 dark:text-accent-soft"
          >
            <i class="bi bi-file-earmark-arrow-down" />
            {{ t('sofax.nav.exportCredits', { n: exportCredits }) }}
            <div class="pointer-events-none absolute right-0 top-full z-10 mt-2 hidden w-56 rounded-[0.625rem] bg-ink px-3 py-2 text-center text-xs font-normal leading-relaxed text-white shadow-lift group-hover:block dark:bg-gray-700">
              {{ t('premium.upsell.oneTimeExport.creditsTooltip', { n: exportCredits }) }}
            </div>
          </div>

          <LocaleLink to="/applications" :class="[linkClass, 'hidden sm:inline-flex']">{{ t('applications.nav') }}</LocaleLink>
          <SofaxBtn to="/cv" :label="t('sofax.nav.myCvs')" variant="accent" class="btn-sm !hidden whitespace-nowrap sm:!inline-flex" />
          <NavbarUserMenu />

          <div :class="mobileOnlyClass">
            <CommonHamburgerMenu v-model:is-open="isMenuOpen" />
          </div>
        </div>
      </template>
    </div>

    <!--
      ===== Off-canvas mobile menu =====
      One panel for both menus: site links first, then the signed-in extras,
      and the primary action (Sign up / My CVs) pinned to the bottom.
    -->
    <template v-if="showPresentationMenu || showAdminMenu">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-300"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isMenuOpen"
          class="fixed inset-0 z-40 bg-ink/60"
          :class="mobileOnlyClass"
          aria-hidden="true"
          @click="closeMenu"
        />
      </Transition>

      <div
        id="navbar-default"
        class="fixed inset-y-0 right-0 z-50 flex w-80 max-w-[85vw] flex-col bg-white shadow-lift transition-transform duration-300 dark:bg-gray-900"
        :class="[mobileOnlyClass, isMenuOpen ? 'translate-x-0' : 'translate-x-full']"
        :aria-hidden="!isMenuOpen"
      >
        <div class="flex items-center justify-between border-b border-line px-5 py-4 dark:border-gray-800">
          <span class="font-title text-lg font-bold text-ink dark:text-white">{{ t('sofax.nav.menuTitle') }}</span>
          <button
            type="button"
            class="inline-flex size-10 items-center justify-center rounded-full text-2xl text-ink hover:bg-surface dark:text-white dark:hover:bg-gray-800"
            :aria-label="t('sofax.nav.closeMenu')"
            @click="closeMenu"
          >
            &times;
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto px-5" :aria-label="t('sofax.nav.menuTitle')">
          <ul class="divide-y divide-line dark:divide-gray-800">
            <li>
              <ClientOnly>
                <NavbarFeaturesDropdown
                  :is-desktop="isFeaturesDropdownDesktop"
                  :is-open="isFeaturesOpen"
                  @update:is-open="isFeaturesOpen = $event"
                  @close="closeMenu"
                />
              </ClientOnly>
            </li>
            <li v-for="link in links" :key="link.id">
              <LocaleLink :to="link.to" :class="mobileLinkClass" @click="closeMenu">
                {{ link.label }}
                <i class="bi bi-arrow-right-short text-2xl text-muted" />
              </LocaleLink>
            </li>
            <li v-if="!isLoggedIn">
              <LocaleLink to="/login" :class="mobileLinkClass" @click="closeMenu">
                {{ t('sofax.nav.login') }}
                <i class="bi bi-arrow-right-short text-2xl text-muted" />
              </LocaleLink>
            </li>
            <li v-if="isLoggedIn">
              <LocaleLink to="/applications" :class="mobileLinkClass" @click="closeMenu">
                <span class="inline-flex items-center gap-2"><i class="bi bi-kanban" />{{ t('applications.nav') }}</span>
                <i class="bi bi-arrow-right-short text-2xl text-muted" />
              </LocaleLink>
            </li>
            <li v-if="isLoggedIn && !isPremium && !isLoading">
              <LocaleLink to="/admin/premium" :class="mobileLinkClass" @click="closeMenu">
                <span class="inline-flex items-center gap-2"><i class="bi bi-star-fill text-yellow-400" />{{ t('sofax.nav.premium') }}</span>
                <i class="bi bi-arrow-right-short text-2xl text-muted" />
              </LocaleLink>
            </li>
            <li v-if="isLoggedIn && !isLoading && exportCredits > 0" class="flex items-center gap-2 py-4 font-body text-sm font-semibold text-body dark:text-gray-300">
              <i class="bi bi-file-earmark-arrow-down" />
              {{ t('sofax.nav.exportCredits', { n: exportCredits }) }}
            </li>
          </ul>
        </nav>

        <div class="space-y-4 border-t border-line p-5 dark:border-gray-800">
          <SofaxBtn
            v-if="!isLoggedIn"
            :label="t('sofax.nav.buildCv')"
            variant="accent"
            block
            @click="closeMenu(); router.push(localize('/free-cv-builder'))"
          />
          <SofaxBtn
            v-else
            :label="t('sofax.nav.myCvs')"
            variant="accent"
            block
            @click="closeMenu(); router.push('/cv')"
          />
          <div class="flex justify-center">
            <NavbarColorModeSwitcher />
          </div>
        </div>
      </div>
    </template>
  </header>
</template>
